import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Collapse,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  AutoAwesome as AutoAwesomeIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useVoiceRecording } from '../../hooks/useVoiceRecording';
import VoiceTranscriptDisplay from './VoiceTranscriptDisplay';
import { VoiceToSoapRequest, VoiceToSoapResponse } from '../../types/voice';
import { soapNotesService } from '../../services/soapNotesService';
import { SoapSubjective, SoapObjective, SoapAssessment, SoapPlan } from '../../types/soap';
import api from '../../utils/api';
import { useAnalytics } from '../../hooks/useAnalytics';

interface VoiceSoapRecorderProps {
  horseId: string;
  horseName?: string;
  horseBreed?: string;
  horseAge?: number;
  onSoapGenerated: (data: {
    subjective: SoapSubjective;
    objective: SoapObjective;
    assessment: SoapAssessment;
    plan: SoapPlan;
    originalTranscript?: string;
    templateValues?: Record<string, any>;
  }) => void;
  isSubscribed?: boolean;
  subscriptionTier?: string;
  templateSchema?: any[];
}

const VoiceSoapRecorder: React.FC<VoiceSoapRecorderProps> = ({
  horseId,
  horseName,
  horseBreed,
  horseAge,
  onSoapGenerated,
  isSubscribed = true,
  subscriptionTier = 'PROFESSIONAL',
  templateSchema,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { trackFeatureUsed } = useAnalytics();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [isExpanded, setIsExpanded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [autoProcess, setAutoProcess] = useState(false);
  const [enableEditMode, setEnableEditMode] = useState(false);
  
  // Voice recording settings
  const [settings, setSettings] = useState({
    continuous: true,
    interimResults: true,
    maxSilenceDuration: 3000,
    language: 'en-US',
    autoRestart: true,
  });

  const {
    state,
    segments,
    start,
    pause,
    resume,
    stop,
    clearTranscript,
    isSupported,
  } = useVoiceRecording(settings);

  // Custom transcript state to allow editing
  const [editableTranscript, setEditableTranscript] = useState(state.transcript);

  // Check if feature is available based on subscription
  // TEMPORARILY DISABLED FOR TESTING
  const isFeatureAvailable = true; // Always enabled for testing
  // const isFeatureAvailable = isSubscribed && 
  //   ['PROFESSIONAL', 'ENTERPRISE'].includes(subscriptionTier || '');

  // Update editable transcript when voice recording changes
  useEffect(() => {
    setEditableTranscript(state.transcript);
  }, [state.transcript]);

  // Process transcript with AI
  const processTranscript = useCallback(async () => {
    const transcriptToProcess = editableTranscript || state.transcript;
    if (!transcriptToProcess) {
      enqueueSnackbar('No transcript to process', { variant: 'warning' });
      return;
    }


    setIsProcessing(true);
    try {
      const request: VoiceToSoapRequest = {
        transcript: transcriptToProcess,
        horseId,
        horseName,
        horseBreed,
        horseAge,
        sessionId: state.sessionId || undefined,
        templateSchema: templateSchema?.length ? templateSchema : undefined,
      };

      const response = await soapNotesService.processVoiceToSoap(request);

      if (response.success) {
        trackFeatureUsed('voice_to_soap_completed');
        onSoapGenerated({
          ...response.data,
          templateValues: response.data.templateValues,
        });
        enqueueSnackbar('SOAP note generated successfully', { variant: 'success' });
        
        // Show any warnings
        if (response.data.warnings?.length) {
          response.data.warnings.forEach(warning => {
            enqueueSnackbar(warning, { variant: 'warning' });
          });
        }

        // Keep transcript visible for review; do not auto-clear
        // If you want to clear manually, use the "Clear" button.
      } else {
        throw new Error(response.message || 'Failed to process transcript');
      }
    } catch (error: any) {
      console.error('Error processing transcript:', error);
      // The AI route answers soft-200 with the reason in `message`, which the branch above re-throws as a
      // plain Error; reading only the response body dropped it, so a stopped service and a bad transcript
      // looked the same (from #652).
      const reason = error.response?.data?.message || error.message || '';
      const unreachable = /ECONNREFUSED|ENOTFOUND|ETIMEDOUT|socket hang up/i.test(reason);
      enqueueSnackbar(
        unreachable
          ? 'The AI service is not responding. Enter the note by hand, or check with your administrator.'
          : reason || 'Failed to generate SOAP note from transcript',
        { variant: 'error', autoHideDuration: 10000 },
      );
    } finally {
      setIsProcessing(false);
    }
  }, [editableTranscript, state.transcript, state.sessionId, horseId, horseName, horseBreed, horseAge,
      onSoapGenerated, enqueueSnackbar, autoProcess, clearTranscript, templateSchema]);

  // Auto-process when recording stops if enabled
  useEffect(() => {
    if (autoProcess && !state.isRecording && state.transcript && !isProcessing) {
      processTranscript();
    }
  }, [state.isRecording, autoProcess]);

  // Handle recording toggle
  const handleRecordingToggle = () => {
    if (state.isRecording) {
      stop();
    } else {
      start();
      setIsExpanded(true);
    }
  };

  // Handle pause/resume
  const handlePauseResume = () => {
    if (state.isPaused) {
      resume();
    } else {
      pause();
    }
  };

  // Handle edit button click
  const handleEditClick = () => {
    setIsExpanded(true);
    setEnableEditMode(true);
  };

  // Check browser support
  if (!isSupported) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Voice recording is not supported in your browser. Please use Chrome, Edge, or Safari.
      </Alert>
    );
  }

  // Check subscription - DISABLED FOR TESTING
  if (!isFeatureAvailable) {
    return (
      <Alert 
        severity="info" 
        sx={{ my: 2 }}
        action={
          <Button color="inherit" size="small" href="/settings/subscription">
            Upgrade
          </Button>
        }
      >
        More to come...
      </Alert>
    );
  }

  return (
    <Box sx={{ my: 2 }}>
      {/* Main Controls */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          bgcolor: state.isRecording ? 'error.light' : 'background.paper',
          transition: 'background-color 0.3s',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: isExpanded ? 2 : 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, flex: 1, mr: { xs: 2, sm: 3 } }}>
            {/* Main Record Button */}
            <Tooltip title={state.isRecording ? 'Stop recording' : 'Start voice recording'}>
              <IconButton
                onClick={handleRecordingToggle}
                color={state.isRecording ? 'error' : 'primary'}
                size={isMobile ? "medium" : "large"}
                sx={{
                  bgcolor: state.isRecording ? 'white' : 'primary.light',
                  '&:hover': {
                    bgcolor: state.isRecording ? 'grey.100' : 'primary.main',
                    color: 'white',
                  },
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  transition: 'all 0.3s',
                  animation: state.isRecording && !state.isPaused 
                    ? 'pulse 2s infinite' 
                    : 'none',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.1)' },
                    '100%': { transform: 'scale(1)' },
                  },
                }}
              >
                {state.isRecording ? <MicOffIcon /> : <MicIcon fontSize={isMobile ? "medium" : "large"} />}
              </IconButton>
            </Tooltip>

            {/* Pause/Resume Button */}
            {state.isRecording && (
              <Tooltip title={state.isPaused ? 'Resume recording' : 'Pause recording'}>
                <IconButton
                  onClick={handlePauseResume}
                  color="primary"
                  sx={{ border: '1px solid', borderColor: 'primary.main' }}
                >
                  {state.isPaused ? <PlayIcon /> : <PauseIcon />}
                </IconButton>
              </Tooltip>
            )}

            {/* Status Indicator */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="subtitle1" 
                fontWeight="medium"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.2 }}
              >
              Voice-to-SOAP
              </Typography>
              {state.isRecording ? (
                <Chip
                  label={state.isPaused ? 'Paused' : 'Recording...'}
                  size="small"
                  color={state.isPaused ? 'warning' : 'error'}
                  icon={<MicIcon />}
                  sx={{ height: 20, mt: 0.5 }}
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Click mic to start
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            {/* Process Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={processTranscript}
              disabled={(!editableTranscript && !state.transcript) || isProcessing || state.isRecording}
              startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              size={isMobile || isTablet ? 'small' : 'medium'}
              sx={{
                minHeight: 40,
                height: 40,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                }
              }}
            >
              {isProcessing ? 'Processing' : 'Generate'}
            </Button>

            {/* Icons Group */}
            <Box sx={{ display: 'flex' }}>
              {/* Edit Button */}
              <Tooltip title="Click to type directly">
                <IconButton 
                  onClick={handleEditClick}
                  disabled={state.isRecording}
                  color={enableEditMode ? "primary" : "default"}
                  size={isMobile ? "small" : "medium"}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Settings Button */}
              <Tooltip title="Settings">
                <IconButton 
                  onClick={() => setShowSettings(true)}
                  size={isMobile ? "small" : "medium"}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Info Button */}
              <Tooltip title="How to use">
                <IconButton 
                  onClick={() => setShowInfo(true)}
                  size={isMobile ? "small" : "medium"}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Expandable Transcript Display */}
        <Collapse in={isExpanded || state.isRecording}>
          <VoiceTranscriptDisplay
            transcript={editableTranscript}
            segments={segments}
            isRecording={state.isRecording}
            isPaused={state.isPaused}
            isProcessing={isProcessing}
            recordingDuration={state.recordingDuration}
            onClear={() => {
              clearTranscript();
              setEditableTranscript('');
            }}
            onTranscriptChange={setEditableTranscript}
            maxHeight={300}
            enableEditMode={enableEditMode}
            onEditModeChange={setEnableEditMode}
          />
        </Collapse>

        {/* Error Display */}
        {state.error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => {}}>
            {state.error}
          </Alert>
        )}
      </Paper>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onClose={() => setShowSettings(false)}>
        <DialogTitle>Voice Recording Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoProcess}
                  onChange={(e) => setAutoProcess(e.target.checked)}
                />
              }
              label="Auto-generate SOAP when recording stops"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.continuous}
                  onChange={(e) => setSettings({ ...settings, continuous: e.target.checked })}
                />
              }
              label="Continuous recording"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.interimResults}
                  onChange={(e) => setSettings({ ...settings, interimResults: e.target.checked })}
                />
              }
              label="Show interim results"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoRestart}
                  onChange={(e) => setSettings({ ...settings, autoRestart: e.target.checked })}
                />
              }
              label="Auto-restart on errors"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettings(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog open={showInfo} onClose={() => setShowInfo(false)} maxWidth="sm">
        <DialogTitle>How to Use Voice-to-SOAP</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2">
              <strong>1. Start Recording:</strong> Click the microphone button to begin recording your examination notes.
            </Typography>
            <Typography variant="body2">
              <strong>2. Speak Naturally:</strong> Describe your findings as you normally would. The AI will understand veterinary terminology and context.
            </Typography>
            <Typography variant="body2">
              <strong>3. Pause/Resume:</strong> Use the pause button if you need to interrupt the recording.
            </Typography>
            <Typography variant="body2">
              <strong>4. Generate SOAP:</strong> Click "Generate SOAP" to process your transcript. The AI will organize your notes into proper SOAP format.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Tips:</strong>
              <ul>
                <li>Speak clearly and mention specific findings</li>
                <li>Include vital signs, observations, and treatment plans</li>
                <li>The AI will automatically categorize information into the correct sections</li>
                <li>Review and edit the generated SOAP note before finalizing</li>
              </ul>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInfo(false)}>Got it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VoiceSoapRecorder;
